import { put, call, select, takeLatest } from 'redux-saga/effects';
import { login, userValidate, userLogout } from 'src/services/login.service';
import { push } from 'redux-first-history';
import cookie from 'react-cookies';
import { DASHBOARD_URL, NEW_DASHBOARD_URL, CONSENT_URL, noData, COOKIES } from 'src/constants/constants.js';
import * as loginActions from './loginActions';
import * as loginTypes from './loginTypes';
import { LOGINPAGE, LOGINSSOPAGE } from './constants';
import { StorageService } from 'src/helpers/localStorageService';
import { redirectUrls } from 'src/constants/redirectUrls';
import { DEFAULT_DASHBOARD_VIEW, DEFAULT_SESSION_KEY } from '../../../../constants/constants';
import { fnIsDefaultOldView } from 'src/helpers/commonFunctions';

const portalSsoEnabled = process.env.REACT_APP_PORTAL_SSO_ENABLED;
const ssoEnabled = portalSsoEnabled === 'true';

const loginError = errorObject => {
  return errorObject.response ? errorObject.response : errorObject;
};

const redirectToLogin = ssoRedirectUrl => {
  console.debug('redirectToLogin');
  console.debug(`Redirect: ${ssoRedirectUrl}`);
  const loginPath = (ssoEnabled && redirectUrls && redirectUrls.ssoPath) || '/';
  let logoutPath = (ssoEnabled && redirectUrls && redirectUrls.ssoPathLogout) || '/';
  if (window.location.pathname !== loginPath && window.location.pathname !== logoutPath) {
    if (window.location.search.includes('mode')) {
      const mode = window.location.search.slice(-1);
      logoutPath += mode;
      window.location.replace(logoutPath);
    } else {
      window.location.replace(ssoRedirectUrl ? ssoRedirectUrl : loginPath);
    }
  }
};

export function* loginRequest({ username, password }) {
  console.debug('loginRequest');
  try {
    if (!username || !password) throw loginError(noData);

    let { status, data, error } = yield call(login, username, password);
    if (status !== 200) throw loginError(error);

    ({ status, data, error } = yield call(userValidate));

    const { profileKey, userName, isDualRole, isConsentAcceptanceNeeded, consentList } = data.data;
    if (!data.success || data.success === 'false') throw loginError(error);

    const loginPage = LOGINPAGE;
    yield put(
      loginActions.loginSuccess({
        email: userName,
        userSuccess: true,
        profileKey: profileKey,
        loginPage,
        isConsentAcceptanceNeeded,
        consentList,
      }),
    );
    yield put(loginActions.clearErrors());

    sessionStorage.setItem('userInfo', JSON.stringify(data.data));

    yield put(loginActions.userValidateSuccess({ userInfo: data.data }));
    yield put(loginActions.updateUserRoles({ userInfo: data.data }));

    if (isDualRole) {
      return yield put(loginActions.changeRoleSelection(true));
    }

    if (isConsentAcceptanceNeeded === true) {
      sessionStorage.setItem('CN', 'CN');
      return yield put(push(CONSENT_URL));
    }
    let isDefaultOldView = fnIsDefaultOldView();
    sessionStorage.setItem(DEFAULT_SESSION_KEY, isDefaultOldView ? DEFAULT_DASHBOARD_VIEW.EXISTING : DEFAULT_DASHBOARD_VIEW.NEW);
    return yield put(push(isDefaultOldView ? DASHBOARD_URL : NEW_DASHBOARD_URL));
  } catch (error) {
    const data = error && error.message ? error : { message: error || 'Login failed' };
    yield put(loginActions.loginFailure(data));
  }
}

export function* loginSsoRequest() {
  console.debug('loginSsoRequest');
  try {
    const statusCookie = cookie.load(COOKIES.STATUS);

    //TEMP for UAT SSO
    if (statusCookie && statusCookie === 'DISABLED') {
      // Do nothing; leave them on blank login page
      return;
    }

    // Validate user; should be successful if (httponly) auth cookie is present
    const { status, data, error } = yield call(userValidate);

    if (status === 401) {
      if (ssoEnabled && redirectUrls && redirectUrls.ssoPath) {
        // check if redirecting from CPS app
        console.debug(`Referrer: ${document.referrer}`);

        return redirectToLogin();
      } else {
        // If we don't have an SSO path, assume local dev and redirect to forms login page
        yield put(loginActions.loginSsoFailure());
        yield put(loginActions.gotoPage(LOGINPAGE));
        return redirectToLogin();
      }
    } else if (status !== 200) {
      throw loginError(error);
    }

    const { profileKey, userName, isDualRole, isConsentAcceptanceNeeded, consentList } = data.data;

    if (data.success === true) {
      yield put(
        loginActions.loginSsoSuccess({
          email: userName,
          userSuccess: data.success,
          profileKey: profileKey,
          LOGINSSOPAGE,
          isConsentAcceptanceNeeded,
          consentList,
        }),
      );
      yield put(loginActions.clearErrors());

      sessionStorage.setItem('userInfo', JSON.stringify(data.data));

      yield put(loginActions.userValidateSuccess({ userInfo: data.data }));
      yield put(loginActions.updateUserRoles({ userInfo: data.data }));

      if (isDualRole) {
        return yield put(loginActions.changeRoleSelection(true));
      }

      if (isConsentAcceptanceNeeded === true) {
        sessionStorage.setItem('CN', 'CN');
        return yield put(push(CONSENT_URL));
      }

      if (window.top.location.pathname === '/') {
        let isDefaultOldView = fnIsDefaultOldView();
        sessionStorage.setItem(DEFAULT_SESSION_KEY, isDefaultOldView ? DEFAULT_DASHBOARD_VIEW.EXISTING : DEFAULT_DASHBOARD_VIEW.NEW);

        return yield put(push(isDefaultOldView ? DASHBOARD_URL : NEW_DASHBOARD_URL));
      }
    } else {
      //TODO: shouldn't really happen, but probably need to handle this better
      yield put(loginActions.loginSsoFailure({ message: 'Could not find Profile for user.' }));
    }
  } catch (error) {
    const data = error && error.message ? error : { message: error || 'Login failed' };
    yield put(loginActions.loginSsoFailure(data));
  }
}

export function* validateToken() {
  console.debug('validateToken');
  const isAuthenticated = yield select(state => state.login.isAuthenticated);
  if (!isAuthenticated) {
    return false;
  }
  const { status, data, error } = yield call(userValidate);
  if (status !== 200) throw loginError(error);
  if (data.success && data.success === true) {
    sessionStorage.setItem('userInfo', JSON.stringify(data.data));
    yield put(loginActions.userValidateSuccess({ userInfo: data.data }));
    yield put(loginActions.updateUserRoles({ userInfo: data.data }));
    return true;
  } else {
    yield put(loginActions.clearSession());
    yield put(loginActions.loginFailure({ message: 'Session expired, please log in again.' }));
  }
  return false;
}

export function* userLogoutToken() {
  console.debug('userLogoutToken');
  console.debug('logging out...');
  //remove dashboardType session
  sessionStorage.removeItem('dashboardType');
  sessionStorage.removeItem(DEFAULT_SESSION_KEY);
  let mode = '';
  var entryCookie = cookie.load(COOKIES.ENTRY_PATH);
  if (entryCookie !== null) {
    cookie.remove(COOKIES.ENTRY_PATH);
  }
  if (window.location.search.includes('mode')) {
    mode = window.location.search.slice(-1);
  }
  const { status, data } = yield call(userLogout, { mode });
  console.debug(`logout status: ${status}`);
  // If auth cookie is expired, logout will return a 401
  if (status !== 200 && status !== 401) return false;
  if (status === 401 || (data.success && data.success === true)) {
    console.debug('clearing local data...');
    let storageService = new StorageService();
    yield put(loginActions.clearSession());
    storageService.removeItem('userId');
    storageService.removeItem('pageName');
    console.debug('redirecing user...');
    redirectToLogin();
    return true;
  } else {
    return false;
  }
}

export function* clearAll() {
  //remove dashboardType session
  sessionStorage.removeItem('dashboardType');
  yield put(loginActions.clearErrors());
}

const myLoginSaga = [
  takeLatest(loginTypes.POST_LOGIN_PENDING, loginRequest),
  takeLatest(loginTypes.POST_LOGIN_SSO_PENDING, loginSsoRequest),
  takeLatest(loginTypes.USER_VALIDATE_TOKEN, validateToken),
  takeLatest(loginTypes.RETURN_TO_LOGIN, clearAll),
  takeLatest(loginTypes.GOTO_LOGIN, clearAll),
  takeLatest(loginTypes.LOGOUT_SESSION, userLogoutToken),
];

export default myLoginSaga;
